import { create, tsx } from '@dojo/framework/core/vdom';
import { Head } from '../../widgets/Head';
import { Hero } from '../../widgets/Hero';
import * as css from './CaseStudy.m.css';
import * as summaryCss from '../../widgets/Summary.m.css';
import * as commonCss from '../../Common.m.css';
import { Image } from '../../widgets/Image';
import ImageSection from '../../widgets/ImageSection';
import { Text } from '../../widgets/Text';
import ContentWrapper from '../../widgets/ContentWrapper';
import Section from '../../widgets/Section';
import Technologies from '../../widgets/Technologies';
import SocialCaseStudyList from './SocialCaseStudyList';

const skTripWizardTop = require('../../assets/images/work-mockups/sk-trip-wizard-card-top-left.png');
const skTripWizardTopHighRes = require('../../assets/images/work-mockups/sk-trip-wizard-card-top-left@2x.png');
const skTripWizardMain = require('../../assets/images/work-mockups/sk-trip-wizard-mockup.png');
const skTripWizardMainHighRes = require('../../assets/images/work-mockups/sk-trip-wizard-mockup@2x.png');
const skTripWizardBottomLeft = require('../../assets/images/work-mockups/sk-trip-wizard-card-bottom-left.png');
const skTripWizardBottomLeftHighRes = require('../../assets/images/work-mockups/sk-trip-wizard-card-bottom-left@2x.png');
const skTripWizardBottomRight = require('../../assets/images/work-mockups/sk-trip-wizard-card-bottom-right.png');
const skTripWizardBottomRightHighRes = require('../../assets/images/work-mockups/sk-trip-wizard-card-bottom-right@2x.png');

const factory = create();

export const CaseStudySocialTripWizard = factory(function CaseStudySocialTripWizard() {
	return (
		<div classes={css.root}>
			<Head
				title="Using React to Update Social Knowledge&#039;s Platforms | SitePen"
				description="See how our team at SitePen helped Social Knowledge streamline and accelerate their modernization efforts to quickly reduce maintenance costs."
				noindex
			/>

			<Hero background="level2BlueGreen" align="center" paddingBottom="sm">
				{{
					title: 'Case Study',
					xlTitle: 'Social Knowledge',
					subtitle: 'Project: Trip Wizard',
					blurb: (
						<virtual>
							<span>
								It’s rare that a technology startup successfully creates a product
								that solves real-world problems and engages customers on their first
								try. So what happens when that very success creates growing pains
								for the company?
							</span>
							<Technologies
								icons={[
									'react',
									'react-native',
									'redux',
									'mobx',
									'typescript',
									'jest'
								]}
							></Technologies>
						</virtual>
					)
				}}
			</Hero>

			<ContentWrapper size="small">
				<Text type="section">The Situation</Text>
				<Text type="body">
					As the state of web application development changes and matures, products built
					on previous-generation technologies reach their end of life and need to get
					rebuilt to gain significant advantages of the web platform. Social Knowledge was
					faced with that and wanted to make sure its improvements were the right ones
					that would position the app for continuous modernization into the future.
				</Text>
			</ContentWrapper>
			<ImageSection shaded paddingTop="lg" contentBias>
				{{
					title: 'The Solution',
					blurb: (
						<div classes={[css.stackedBlurb]}>
							<p>
								SitePen provided the leadership and cross-functional expertise
								needed to execute on a complete rewrite of the architecture that
								improved performance, maintainability, and modernity of the
								application.
							</p>
							<p>
								Considering Social Knowledge’s long-term goal of having both a
								responsive website and moving into native apps in the future,
								SitePen leveraged the React Native Web library. This library makes
								native component syntax available for both web-based apps and
								isolated business logic, helping Social Knowledge increase future
								efficiency by re-using this architecture for future native
								application development.
							</p>
							<p>
								Leveraging the Jest JavaScript testing framework, SitePen authored
								unit tests for state logic and snapshot tests for widgets, providing
								solid test coverage for prop-driven components. The snapshot results
								automatically get saved, allowing for comparison when re-running
								tests after each new feature gets implemented, giving Social
								Knowledge’s developers confidence that changes would not introduce
								regressions.
							</p>
						</div>
					),
					images: {
						main: (
							<div classes={[css.stackedMainImage]}>
								<Image
									src={skTripWizardMain}
									highResSrc={skTripWizardMainHighRes}
									shadow="light"
									responsive={{
										medium: { shadow: 'dark' }
									}}
								/>
							</div>
						),
						top: (
							<Image
								src={skTripWizardTop}
								highResSrc={skTripWizardTopHighRes}
								shadow="dark"
								size="lg"
							/>
						),
						bottom: {
							left: (
								<Image
									src={skTripWizardBottomLeft}
									highResSrc={skTripWizardBottomLeftHighRes}
									shadow="dark"
									size="lg"
								/>
							),
							right: (
								<Image
									src={skTripWizardBottomRight}
									highResSrc={skTripWizardBottomRightHighRes}
									shadow="dark"
									size="lg"
								/>
							)
						}
					}
				}}
			</ImageSection>
			<ContentWrapper>
				<div classes={css.result}>
					<Text type="section">The Result</Text>
					<p classes={[commonCss.paragraphWidth, summaryCss.blurb]}>
						In one big step, SitePen eliminated the weighty technical debt of an
						outdated front-end technology stack and helped put Social Knowledge on a
						sustainable path with better development possibilities in their new React
						environment.
					</p>
				</div>
			</ContentWrapper>
			<Section>
				<ContentWrapper>
					<SocialCaseStudyList caseStudies={['complete', 'life']} />
				</ContentWrapper>
			</Section>
		</div>
	);
});

export default CaseStudySocialTripWizard;
